import {
  assignments,
  stages,
  users,
  workflowContexts,
  workflowResponse,
  workflows,
} from '@opallabs/api/v2'
import type { WorkflowApi } from '../types'
import {
  selectAssignments,
  selectResponses,
  selectStatus,
  selectUser,
  selectWorkflow,
} from './selectors'

const momentFilter = (entityId: string) => {
  if (!/^\d+$/.test(entityId))
    throw new Error(`workflowApiV2 needs a moment legacy_id; got "${entityId}"`)
  return { 'context.moment_id': [parseInt(entityId, 10)] }
}

export const workflowApiV2: WorkflowApi = {
  async readWorkflow({ entityId }) {
    const document = await workflows.readCollection({
      query: {
        filter: momentFilter(entityId),
        expose: { workflow: { meta: 'true' } },
        include: ['stages'],
      },
    })
    return selectWorkflow(document)
  },

  async readWorkflowStatus({ entityId }) {
    const document = await workflows.readCollection({
      query: {
        filter: momentFilter(entityId),
        expose: { workflow: { meta: 'true' } },
        include: ['stages.assignments', 'stages.assignments.responses'],
      },
    })
    return selectStatus(document)
  },

  async createAttachedWorkflow({ entityId, firstStage }) {
    const workflow = await workflows.create({
      params: { query: { include: ['stages'], template: firstStage } },
      requestBody: { data: { type: 'workflow' } },
    })
    await workflowContexts.create({
      requestBody: {
        data: {
          type: 'context',
          attributes: { moment_id: entityId },
          relationships: {
            moment: { data: { id: entityId, type: 'moment' } },
            workflow: { data: { id: workflow.data.id, type: 'workflow' } },
          },
        },
      },
    })
    return { id: workflow.data.id }
  },

  async rewindWorkflow({ workflowId, stageId }) {
    await workflows.rewind({
      params: { path: { workflow_id: workflowId }, query: {} },
      requestBody: {
        data: {
          id: workflowId,
          type: 'workflow',
          relationships: { stage: { data: { id: stageId, type: 'stage' } } },
        },
      },
    })
  },

  async createStage({ workflowId, stageType, position }) {
    const document = await stages.create({
      params: { query: {} },
      requestBody: {
        data: {
          type: 'stage',
          attributes: {
            stage_type: stageType,
            due_date: null,
            position,
            metadata:
              stageType === 'approval' ? { completion_rules: 'all' } : {},
          },
          relationships: {
            workflow: { data: { id: workflowId, type: 'workflow' } },
          },
        },
      },
    })
    return { id: document.data.id }
  },

  async updateStage({ stageId, attributes }) {
    await stages.update({
      params: { path: { stage_id: stageId }, query: {} },
      requestBody: { data: { id: stageId, type: 'stage', attributes } },
    })
  },

  async deleteStage({ stageId }) {
    await stages.delete({ params: { path: { stage_id: stageId }, query: {} } })
  },

  async readAssignmentsByStage({ stageId }) {
    const document = await stages.readSingle({
      path: { stage_id: stageId },
      query: { include: ['assignments'] },
    })
    return selectAssignments(document)
  },

  async createAssignment({ stageId, stageType }) {
    await assignments.create({
      requestBody: {
        data: {
          type: 'assignment',
          attributes: { assignment_type: stageType },
          relationships: {
            stage: { data: { id: stageId, type: 'stage' } },
            assigned_user: { data: null },
          },
        },
      },
    })
  },

  async updateAssignment({ assignmentId, attributes = {}, assignedUserId }) {
    const relationships =
      assignedUserId === undefined
        ? undefined
        : {
            assigned_user: {
              data:
                assignedUserId === null
                  ? null
                  : { id: assignedUserId, type: 'user' as const },
            },
          }
    await assignments.update({
      params: { path: { assignment_id: assignmentId } },
      requestBody: {
        data: {
          id: assignmentId,
          type: 'assignment',
          attributes,
          relationships,
        },
      },
    })
  },

  async deleteAssignment({ assignmentId }) {
    await assignments.delete({
      params: { path: { assignment_id: assignmentId } },
    })
  },

  assigneeIdOf: (user) => user.legacyId,

  async readUser({ userId }) {
    const document = await users.readSingle({
      path: { user_id: userId },
      query: { include: ['avatar_asset_reference'] },
    })
    return selectUser(document)
  },

  async readResponsesByAssignment({ assignmentId, stageType }) {
    const document = await assignments.readSingle({
      path: { assignment_id: assignmentId },
      query: { include: ['responses'] },
    })
    return selectResponses(document, stageType)
  },

  async createResponse({ assignmentId, details }) {
    await workflowResponse.create({
      requestBody: {
        data: {
          type: 'response',
          attributes: { details },
          relationships: {
            assignment: { data: { id: assignmentId, type: 'assignment' } },
          },
        },
      },
    })
  },
}
